import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDemographicEquity } from '@/hooks/ml';
import { CellSuppressionBanner } from '@/components/ml';
import { useDrillDown } from './DrillDownContext';

export const DemographicEquityCard: React.FC = () => {
  const { drillCboId } = useDrillDown();
  const { data, isLoading } = useDemographicEquity(drillCboId);
  const cells = data?.cells ?? [];

  // Aggregate by (language, age_cohort) — heatmap by **send volume** (engagements),
  // not reply rate. The point of this card is showing WHO got reached how often.
  const languages = Array.from(new Set(cells.map((c) => c.member_language))).slice(0, 10);
  const ages = ['<20', '20-29', '30-39', '40-49', '50-59', '60-69', '70+', 'Unknown'];

  const byCell: Record<string, { engagements: number; replies: number; unique_members: number }> = {};
  let maxEngagements = 0;
  for (const c of cells) {
    const key = `${c.member_language}|${c.age_cohort}`;
    const cur = byCell[key];
    const engagements = (cur?.engagements ?? 0) + c.engagements;
    const replies = (cur?.replies ?? 0) + c.replies;
    const unique_members = (cur?.unique_members ?? 0) + c.unique_members;
    byCell[key] = { engagements, replies, unique_members };
    if (engagements > maxEngagements) maxEngagements = engagements;
  }

  function colorForSends(engagements: number): string {
    if (engagements === 0 || maxEngagements === 0) return 'bg-gray-50 text-gray-400';
    const r = engagements / maxEngagements;
    if (r >= 0.85) return 'bg-blue-700 text-white';
    if (r >= 0.6) return 'bg-blue-500 text-white';
    if (r >= 0.35) return 'bg-blue-300';
    if (r >= 0.15) return 'bg-blue-200';
    return 'bg-blue-100';
  }

  // Compact number formatter — k/m for readability in the heatmap cells.
  function compact(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return String(n);
  }

  const totals = (() => {
    let engagements = 0;
    let replies = 0;
    for (const k in byCell) {
      engagements += byCell[k].engagements;
      replies += byCell[k].replies;
    }
    return { engagements, replies };
  })();

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Messages sent — by language × age</CardTitle>
        <div className="text-xs text-gray-500 mt-0.5">
          Where outreach is being concentrated. Color depth = send volume, hover for counts.
          {totals.engagements > 0 && (
            <>
              {' '}Total: <span className="font-medium tabular-nums">{totals.engagements.toLocaleString()}</span> sends
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <CellSuppressionBanner threshold={data?.suppression_threshold ?? 100} />
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : cells.length === 0 ? (
          <div className="text-sm text-gray-500">No demographic data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left font-medium text-gray-600">Language</th>
                  {ages.map((a) => (
                    <th key={a} className="px-2 py-1 text-center font-medium text-gray-600">
                      {a}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-right font-medium text-gray-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {languages.map((lang) => {
                  let rowTotal = 0;
                  ages.forEach((a) => {
                    rowTotal += byCell[`${lang}|${a}`]?.engagements ?? 0;
                  });
                  return (
                    <tr key={lang}>
                      <td className="px-2 py-1 font-medium text-gray-700">{lang}</td>
                      {ages.map((age) => {
                        const c = byCell[`${lang}|${age}`];
                        const n = c?.engagements ?? 0;
                        const cls = colorForSends(n);
                        const title = c
                          ? `${lang} · ${age} — ${n.toLocaleString()} sends, ${c.replies.toLocaleString()} replies, ${c.unique_members.toLocaleString()} unique members`
                          : `${lang} · ${age} — suppressed`;
                        return (
                          <td
                            key={age}
                            title={title}
                            className={`px-2 py-1 text-center tabular-nums rounded-sm border border-white ${cls}`}
                          >
                            {n > 0 ? compact(n) : ''}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1 text-right tabular-nums font-medium text-gray-700">
                        {rowTotal > 0 ? rowTotal.toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
